import { useCallback, useEffect, useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { useModal } from '@store/hooks';

import type { AddonOnboardingEntry } from '../types';

interface OnboardingGateProps {
  entry: AddonOnboardingEntry;
  /** Called when this entry is not due, so the host advances to the next entry. */
  onResolved: () => void;
}

/**
 * Evaluates a single entry. Once its status is known (not loading) it either opens
 * the onboarding modal (eligible) or resolves so the host advances to the next entry.
 *
 * Opening goes through the global modal registry — `open(ADDON_ONBOARDING)` — so
 * GlobalModalManager wraps the flow in a MUI <Dialog> (portal + backdrop + centered
 * Paper). The SteppedModal shell the flow is built on is only modal *content*, so it
 * must be rendered this way to appear as a modal rather than inline in the page.
 *
 * The latch is implicit: once opened, the modal lives in the modal slice independent
 * of this entry's status, so a background status change — e.g. the proxy registration
 * completing mid-flow, which flips the entry to "not due" — does NOT close it. The
 * `decided` ref also stops the gate from re-opening on re-render. It closes only on the
 * user's finish/dismiss (the modal's own close), and re-prompts on the next app mount
 * while still due.
 */
function OnboardingGate({ entry, onResolved }: OnboardingGateProps) {
  const { shouldShow, isLoading } = entry.useStatus();
  const { open } = useModal();
  const decided = useRef(false);

  useEffect(() => {
    if (isLoading || decided.current) return;
    decided.current = true;
    if (shouldShow) {
      open(MODAL_TYPES.ADDON_ONBOARDING, {
        onboardingId: entry.id,
        sx: { borderRadius: 3, overflow: 'hidden' },
      });
      // Deliberately do not advance: one onboarding per session. Any lower-priority
      // entry that is also due re-evaluates and opens on the next app mount.
    } else {
      onResolved();
    }
  }, [isLoading, shouldShow, open, onResolved, entry.id]);

  return null;
}

interface AddOnOnboardingHostProps {
  /** The add-on onboardings to consider, in priority order. RootView passes ADDON_ONBOARDINGS. */
  entries: AddonOnboardingEntry[];
}

/**
 * Opens the first add-on onboarding that's due for the current user — one at a
 * time, registry order = priority. Renders nothing itself; the modal is shown by
 * GlobalModalManager via the modal registry.
 *
 * Mount once in the authenticated shell, AFTER first-run onboarding (see RootView),
 * so add-on onboarding never competes with account creation. Walks entries in
 * priority order, skipping ones that aren't due, and opens the first due one.
 */
export function AddOnOnboardingHost({ entries }: AddOnOnboardingHostProps) {
  const [index, setIndex] = useState(0);
  const onResolved = useCallback(() => setIndex(current => current + 1), []);

  if (index >= entries.length) return null;
  const entry = entries[index];
  return <OnboardingGate key={entry.id} entry={entry} onResolved={onResolved} />;
}
